import React, { useState, useRef, useEffect } from 'react';
import {
  Upload,
  X,
  FileText,
  CheckCircle2,
  Loader2,
  Save,
  AlertCircle,
  Eye,
  ZoomIn,
  ZoomOut,
  RotateCw,
  ExternalLink,
  Calendar,
  FileSearch
} from 'lucide-react';
import Modal from './Modal';
import { ClaudeService } from '../services/claudeService';
import { HireExtractionDraft, ExtractedHireData } from '../types';
import { useToast } from '../context/ToastContext';

interface HireAgreementExtractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExtracted: (data: ExtractedHireData, file: File) => void;
}

const HireAgreementExtractModal: React.FC<HireAgreementExtractModalProps> = ({
  isOpen,
  onClose,
  onExtracted
}) => {
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [drafts, setDrafts] = useState<HireExtractionDraft[]>([]);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState<'upload' | 'review'>('upload');
  const [previewDraftId, setPreviewDraftId] = useState<string | null>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewRotation, setPreviewRotation] = useState(0);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
  const panStartRef = useRef<null | { startX: number; startY: number; originX: number; originY: number }>(null);
  const touchStartRef = useRef<null | { startX: number; startY: number; originX: number; originY: number }>(null);
  const [verifyTarget, setVerifyTarget] = useState<{ draftId: string; field: string } | null>(null);
  const [mobilePreviewExpanded, setMobilePreviewExpanded] = useState(false);
  const [datePicker, setDatePicker] = useState<{ draftId: string; field: 'start_date' | 'end_date' } | null>(null);
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  // --- 1. File Handling ---

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newDrafts: HireExtractionDraft[] = Array.from(files).map(file => ({
      id: Math.random().toString(36).substring(2, 9),
      file,
      fileName: file.name,
      status: 'pending'
    }));

    setDrafts(prev => [...prev, ...newDrafts]);
    setStep('review');

    // Auto-start processing for pending files
    processQueue([...drafts, ...newDrafts]);
  };

  const processQueue = async (currentDrafts: HireExtractionDraft[]) => {
    if (processing) return;
    setProcessing(true);

    const draftsCopy = [...currentDrafts];

    for (let i = 0; i < draftsCopy.length; i++) {
      if (draftsCopy[i].status === 'pending') {
        draftsCopy[i].status = 'processing';
        setDrafts([...draftsCopy]);

        try {
          const result = await ClaudeService.extractHireAgreementData(draftsCopy[i].file);
          draftsCopy[i].status = 'success';
          draftsCopy[i].data = result;
        } catch (err: any) {
          console.error(`Failed to process ${draftsCopy[i].file.name}`, err);
          draftsCopy[i].status = 'error';
          draftsCopy[i].error = err.message || 'Failed to extract data';
        }

        setDrafts([...draftsCopy]);
      }
    }

    setProcessing(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleRemoveDraft = (id: string) => {
    setDrafts(prev => prev.filter(d => d.id !== id));
    if (previewDraftId === id) setPreviewDraftId(null);
    if (drafts.length <= 1) setStep('upload');
  };

  // --- 2. Edit / Review Logic ---

  const handleFieldChange = (id: string, field: string, value: string | boolean) => {
    setDrafts(prev => prev.map(d => {
      if (d.id !== id || !d.data) return d;
      return {
        ...d,
        data: { ...d.data, [field]: value }
      };
    }));
  };

  // --- 3. Final Submission ---

  const handleSaveDraft = (draftId: string) => {
    const draft = drafts.find(d => d.id === draftId);
    if (!draft || !draft.data) {
      addToast('No data to save', 'error');
      return;
    }

    // Validate required fields
    if (!draft.data.vrm || !draft.data.driver_name || !draft.data.start_date) {
      addToast('Please fill in VRM, Driver Name, and Start Date', 'error');
      return;
    }

    // Validate dates
    const startIso = parseUkDateToIso(draft.data.start_date);
    const endIso = draft.data.end_date ? parseUkDateToIso(draft.data.end_date) : null;

    if (!startIso) {
      addToast('Invalid Start Date format', 'error');
      return;
    }

    if (endIso && new Date(endIso) < new Date(startIso)) {
      addToast('End Date cannot be before Start Date', 'error');
      return;
    }

    // Call parent callback with extracted data
    onExtracted(draft.data, draft.file);
    handleClose();
  };

  const handleClose = () => {
    setDrafts([]);
    setPreviewDraftId(null);
    setVerifyTarget(null);
    setMobilePreviewExpanded(false);
    setStep('upload');
    onClose();
  };

  // --- Utility Functions ---

  const isLowConfidence = (draft: HireExtractionDraft, field: string) => {
    return draft.data?.low_confidence_fields?.includes(field);
  };

  const parseUkDateToIso = (raw: string): string | null => {
    const s = (raw || '').trim();
    if (!s) return null;

    // YYYY-MM-DD (allow 1-digit month/day)
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
      const parts = s.split('-').map(Number);
      const yyyy = parts[0];
      const mm = parts[1];
      const dd = parts[2];
      if (!dd || !mm || !yyyy) return null;
      const d = new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0));
      if (!Number.isFinite(d.getTime())) return null;
      if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) return null;
      return d.toISOString();
    }

    // DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY (allow 1-digit day/month; allow 2-digit years)
    const m = s.match(/^(\d{1,2})[\/\-. ](\d{1,2})[\/\-. ](\d{2}|\d{4})$/);
    if (m) {
      const dd = Number(m[1]);
      const mm = Number(m[2]);
      let yyyy = Number(m[3]);
      if (!dd || !mm || !yyyy) return null;
      if (yyyy < 100) yyyy = yyyy >= 80 ? 1900 + yyyy : 2000 + yyyy;
      const d = new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0));
      if (!Number.isFinite(d.getTime())) return null;
      if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) return null;
      return d.toISOString();
    }

    // DDMMYYYY or DDMMYY
    if (/^\d{6}$/.test(s) || /^\d{8}$/.test(s)) {
      const dd = Number(s.slice(0, 2));
      const mm = Number(s.slice(2, 4));
      let yyyy = Number(s.slice(4));
      if (yyyy < 100) yyyy = yyyy >= 80 ? 1900 + yyyy : 2000 + yyyy;
      if (!dd || !mm || !yyyy) return null;
      const d = new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0));
      if (!Number.isFinite(d.getTime())) return null;
      if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) return null;
      return d.toISOString();
    }

    // ISO or other parseable strings
    const d = new Date(s);
    return Number.isFinite(d.getTime()) ? d.toISOString() : null;
  };

  const formatIsoToUkDate = (raw: string): string => {
    const iso = parseUkDateToIso(raw);
    if (!iso) return raw;
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
  };

  const normalizeDateInput = (raw: string): string => {
    const iso = parseUkDateToIso(raw);
    return iso || raw;
  };

  const todayIsoUtc = () => {
    const now = new Date();
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0));
    return d.toISOString();
  };

  const isoToYyyyMmDd = (raw: string): string => {
    const iso = parseUkDateToIso(raw);
    if (!iso) return '';
    const d = new Date(iso);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const yyyyMmDdToIso = (yyyyMmDd: string): string | null => {
    const v = (yyyyMmDd || '').trim();
    if (!v) return null;
    return parseUkDateToIso(v);
  };

  const isInvalidDate = (raw: string | undefined): boolean => {
    const s = String(raw || '').trim();
    if (!s) return false;
    return !parseUkDateToIso(s);
  };

  const openPreview = (draftId: string) => {
    setPreviewDraftId(draftId);
    setPreviewZoom(1);
    setPreviewRotation(0);
    setPreviewPan({ x: 0, y: 0 });
    setMobilePreviewExpanded(false);
    setDatePicker(null);
  };

  const verifyField = (draftId: string, field: string) => {
    openPreview(draftId);
    setVerifyTarget({ draftId, field });
  };

  const previewDraft = previewDraftId ? drafts.find(d => d.id === previewDraftId) : null;
  const previewIsPdf = !!previewDraft?.file?.type?.includes('pdf');
  const previewCanRenderImage = !!previewDraft?.file?.type?.includes('image');
  const previewUrl = previewDraft ? URL.createObjectURL(previewDraft.file) : null;

  const clampZoom = (z: number) => Math.min(4, Math.max(0.5, z));
  const zoomIn = () => setPreviewZoom(z => clampZoom(Number((z + 0.25).toFixed(2))));
  const zoomOut = () => setPreviewZoom(z => clampZoom(Number((z - 0.25).toFixed(2))));
  const resetView = () => {
    setPreviewZoom(1);
    setPreviewRotation(0);
    setPreviewPan({ x: 0, y: 0 });
  };
  const rotate = () => setPreviewRotation(r => (r + 90) % 360);

  useEffect(() => {
    if (!verifyTarget) return;
    const key = `${verifyTarget.draftId}:${verifyTarget.field}`;
    const el = fieldRefs.current[key];
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (el as any).focus?.();
    } catch {
      // ignore
    }
  }, [verifyTarget]);

  useEffect(() => {
    if (!isOpen) setDatePicker(null);
  }, [isOpen]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      drafts.forEach(d => {
        try {
          URL.revokeObjectURL(URL.createObjectURL(d.file));
        } catch {
          // ignore
        }
      });
    };
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Extract Hire Agreement Data"
      maxWidth={step === 'review' ? 'max-w-6xl' : 'max-w-3xl'}
    >
      <div className="flex flex-col h-[70vh]">
        {step === 'review' && (
          <>
            {/* Mobile/Small-screen docked preview */}
            {previewDraft && previewUrl && (
              <div className="lg:hidden px-6 pt-6">
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-950/40 border-b border-slate-200 dark:border-slate-800">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500 dark:text-slate-400">Preview</div>
                      <div className="text-sm font-bold text-slate-900 dark:text-white truncate">
                        {previewDraft.fileName}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setMobilePreviewExpanded(v => !v)}
                        className="px-3 py-1.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                      >
                        {mobilePreviewExpanded ? 'Minimize' : 'Expand'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewDraftId(null)}
                        className="px-3 py-1.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                      >
                        Hide
                      </button>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={zoomOut}
                          className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                        >
                          <ZoomOut size={14} /> Zoom
                        </button>
                        <button
                          type="button"
                          onClick={zoomIn}
                          className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                        >
                          <ZoomIn size={14} /> Zoom
                        </button>
                        <button
                          type="button"
                          onClick={rotate}
                          className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                        >
                          <RotateCw size={14} /> Rotate
                        </button>
                        <button
                          type="button"
                          onClick={resetView}
                          className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        >
                          Reset
                        </button>
                        <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                          {Math.round(previewZoom * 100)}%
                        </div>
                      </div>
                      <a
                        href={previewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                        title="Open in new tab"
                      >
                        <ExternalLink size={14} />
                        Open
                      </a>
                    </div>
                    <div
                      className={`${mobilePreviewExpanded ? 'h-[60vh]' : 'h-56'} w-full bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden`}
                    >
                      {previewIsPdf ? (
                        <iframe title="PDF preview" src={previewUrl} className="w-full h-full" />
                      ) : previewCanRenderImage ? (
                        <div
                          className="w-full h-full relative overflow-hidden touch-none"
                          onMouseDown={(e) => {
                            panStartRef.current = {
                              startX: e.clientX,
                              startY: e.clientY,
                              originX: previewPan.x,
                              originY: previewPan.y
                            };
                          }}
                          onMouseMove={(e) => {
                            if (!panStartRef.current) return;
                            const dx = e.clientX - panStartRef.current.startX;
                            const dy = e.clientY - panStartRef.current.startY;
                            setPreviewPan({ x: panStartRef.current.originX + dx, y: panStartRef.current.originY + dy });
                          }}
                          onMouseUp={() => {
                            panStartRef.current = null;
                          }}
                          onMouseLeave={() => {
                            panStartRef.current = null;
                          }}
                          onTouchStart={(e) => {
                            const t = e.touches[0];
                            if (!t) return;
                            touchStartRef.current = {
                              startX: t.clientX,
                              startY: t.clientY,
                              originX: previewPan.x,
                              originY: previewPan.y
                            };
                          }}
                          onTouchMove={(e) => {
                            const t = e.touches[0];
                            if (!t || !touchStartRef.current) return;
                            const dx = t.clientX - touchStartRef.current.startX;
                            const dy = t.clientY - touchStartRef.current.startY;
                            setPreviewPan({
                              x: touchStartRef.current.originX + dx,
                              y: touchStartRef.current.originY + dy
                            });
                          }}
                          onTouchEnd={() => {
                            touchStartRef.current = null;
                          }}
                        >
                          <img
                            src={previewUrl}
                            alt="Preview"
                            className="absolute left-1/2 top-1/2 max-w-none select-none"
                            draggable={false}
                            style={{
                              transform: `translate(calc(-50% + ${previewPan.x}px), calc(-50% + ${previewPan.y}px)) scale(${previewZoom}) rotate(${previewRotation}deg)`,
                              transformOrigin: 'center'
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500 dark:text-slate-400">
                          Preview not available for this file type.
                        </div>
                      )}
                    </div>
                    {!previewIsPdf && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                        Tip: drag to pan • use zoom/rotate to verify flagged fields
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Step 1: Upload Zone */}
        {step === 'upload' && (
          <div
            className="flex-1 flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 m-6 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800/80 hover:border-primary-400 dark:hover:border-primary-500"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="p-4 bg-white dark:bg-slate-700 rounded-full shadow-sm mb-4">
              <FileSearch className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 dark:text-white">Upload Hire Agreement</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6 text-center max-w-xs">
              Upload hire agreement documents (PDF, JPG, PNG). Our AI will extract driver details, dates, and vehicle
              information automatically.
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Select Files
            </button>
            <input
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="hidden"
              ref={fileInputRef}
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>
        )}

        {/* Step 2: Review Extracted Data */}
        {step === 'review' && (
          <div className="flex flex-col h-full overflow-hidden">
            {/* Toolbar */}
            <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900 shrink-0">
              <div className="flex items-center gap-2">
                {processing ? (
                  <span className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 font-medium bg-primary-50 dark:bg-primary-900/20 px-3 py-1 rounded-full">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Processing {drafts.filter(d => d.status === 'processing' || d.status === 'pending').length}{' '}
                    files...
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-full">
                    <CheckCircle2 className="w-3 h-3" />
                    Analysis Complete
                  </span>
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-xs font-medium text-slate-500 hover:text-primary-600 dark:text-slate-400 dark:hover:text-primary-400 flex items-center gap-1"
              >
                <Upload size={14} /> Add more
              </button>
            </div>

            {/* Scrollable List */}
            <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50 dark:bg-slate-950">
                {drafts.map((draft) => (
                  <div
                    key={draft.id}
                    className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col md:flex-row gap-6 animate-in fade-in slide-in-from-bottom-2 ${
                      previewDraftId === draft.id ? 'ring-2 ring-primary-500/40' : ''
                    }`}
                  >
                    {/* Preview Thumbnail */}
                    <div className="w-full md:w-32 h-32 shrink-0 bg-slate-100 dark:bg-slate-800 rounded-lg overflow-hidden relative border border-slate-200 dark:border-slate-700">
                      {draft.file.type.includes('image') ? (
                        <img
                          src={URL.createObjectURL(draft.file)}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 dark:text-slate-500">
                          <FileText size={32} />
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => openPreview(draft.id)}
                        className="absolute bottom-2 left-2 right-2 px-2 py-1 bg-slate-900/80 text-white text-[10px] font-bold rounded-md hover:bg-slate-900 transition-colors flex items-center justify-center gap-1"
                        title="Open larger preview"
                      >
                        <Eye size={12} />
                        Preview
                      </button>

                      {/* Status Overlay */}
                      {draft.status === 'processing' && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Loader2 className="w-8 h-8 text-white animate-spin" />
                        </div>
                      )}
                      {draft.status === 'error' && (
                        <div className="absolute inset-0 bg-red-500/10 flex items-center justify-center border-2 border-red-500">
                          <AlertCircle className="w-8 h-8 text-red-600" />
                        </div>
                      )}
                    </div>

                    {/* Form / Data */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex flex-col">
                          <h4
                            className="font-medium text-slate-800 dark:text-white truncate max-w-[200px]"
                            title={draft.fileName}
                          >
                            {draft.fileName}
                          </h4>
                          {draft.status === 'error' && <p className="text-xs text-red-500">{draft.error}</p>}
                        </div>
                        <button
                          onClick={() => handleRemoveDraft(draft.id)}
                          className="text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 p-1"
                        >
                          <X size={16} />
                        </button>
                      </div>

                      {draft.status === 'success' && draft.data && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                            {/* VRM */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  VRM *
                                </label>
                                {isLowConfidence(draft, 'vrm') ? (
                                  <button
                                    type="button"
                                    onClick={() => verifyField(draft.id, 'vrm')}
                                    className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap"
                                  >
                                    Verify
                                  </button>
                                ) : (
                                  <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                    Verify
                                  </span>
                                )}
                              </div>
                              <input
                                type="text"
                                value={draft.data.vrm}
                                onChange={(e) =>
                                  handleFieldChange(draft.id, 'vrm', e.target.value.toUpperCase().replace(/\s/g, ''))
                                }
                                ref={(el) => {
                                  fieldRefs.current[`${draft.id}:vrm`] = el;
                                }}
                                className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 font-mono font-bold uppercase focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                  isLowConfidence(draft, 'vrm')
                                    ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                    : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                                } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'vrm' ? 'ring-2 ring-primary-500/30' : ''}`}
                              />
                            </div>

                            {/* Section Divider: Owner Information */}
                            <div className="col-span-2 pt-3 pb-1 border-t border-slate-200 dark:border-slate-700">
                              <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                                Vehicle Owner Information
                              </h4>
                              <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-0.5">
                                For agency model operations. If empty, company name will be used as owner (fleet-owned model).
                              </p>
                            </div>

                            {/* Owner Name */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  Owner Name
                                </label>
                                {isLowConfidence(draft, 'vehicle_owner_name') ? (
                                  <button
                                    type="button"
                                    onClick={() => verifyField(draft.id, 'vehicle_owner_name')}
                                    className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap"
                                  >
                                    Verify
                                  </button>
                                ) : (
                                  <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                    Verify
                                  </span>
                                )}
                              </div>
                              <input
                                type="text"
                                value={draft.data.vehicle_owner_name || ''}
                                onChange={(e) => handleFieldChange(draft.id, 'vehicle_owner_name', e.target.value)}
                                ref={(el) => {
                                  fieldRefs.current[`${draft.id}:vehicle_owner_name`] = el;
                                }}
                                placeholder="Optional"
                                className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                  isLowConfidence(draft, 'vehicle_owner_name')
                                    ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                    : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                                } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'vehicle_owner_name' ? 'ring-2 ring-primary-500/30' : ''}`}
                              />
                            </div>

                            {/* Owner Email */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  Owner Email
                                </label>
                                {isLowConfidence(draft, 'vehicle_owner_email') ? (
                                  <button
                                    type="button"
                                    onClick={() => verifyField(draft.id, 'vehicle_owner_email')}
                                    className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap"
                                  >
                                    Verify
                                  </button>
                                ) : (
                                  <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                    Verify
                                  </span>
                                )}
                              </div>
                              <input
                                type="email"
                                value={draft.data.vehicle_owner_email || ''}
                                onChange={(e) => handleFieldChange(draft.id, 'vehicle_owner_email', e.target.value)}
                                ref={(el) => {
                                  fieldRefs.current[`${draft.id}:vehicle_owner_email`] = el;
                                }}
                                placeholder="Optional"
                                className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                  isLowConfidence(draft, 'vehicle_owner_email')
                                    ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                    : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                                } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'vehicle_owner_email' ? 'ring-2 ring-primary-500/30' : ''}`}
                              />
                            </div>

                            {/* Owner Phone */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  Owner Phone
                                </label>
                                {isLowConfidence(draft, 'vehicle_owner_phone') ? (
                                  <button
                                    type="button"
                                    onClick={() => verifyField(draft.id, 'vehicle_owner_phone')}
                                    className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap"
                                  >
                                    Verify
                                  </button>
                                ) : (
                                  <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                    Verify
                                  </span>
                                )}
                              </div>
                              <input
                                type="tel"
                                value={draft.data.vehicle_owner_phone || ''}
                                onChange={(e) => handleFieldChange(draft.id, 'vehicle_owner_phone', e.target.value)}
                                ref={(el) => {
                                  fieldRefs.current[`${draft.id}:vehicle_owner_phone`] = el;
                                }}
                                placeholder="Optional"
                                className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                  isLowConfidence(draft, 'vehicle_owner_phone')
                                    ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                    : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                                } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'vehicle_owner_phone' ? 'ring-2 ring-primary-500/30' : ''}`}
                              />
                            </div>

                            {/* Owner Authorization */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  Owner Authorization
                                </label>
                              </div>
                              <div className={`flex items-center h-10 px-3 rounded ${draft.data.owner_authorization_present ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'}`}>
                                {draft.data.owner_authorization_present ? (
                                  <>
                                    <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                                    <span className="text-xs font-medium text-green-700 dark:text-green-300">Detected</span>
                                  </>
                                ) : (
                                  <>
                                    <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mr-2" />
                                    <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Not detected - verify manually</span>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Section Divider: Driver Information */}
                            <div className="col-span-2 pt-3 pb-1 border-t border-slate-200 dark:border-slate-700">
                              <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                                Driver Information
                              </h4>
                            </div>

                            {/* Driver Name */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  Driver Name *
                                </label>
                                {isLowConfidence(draft, 'driver_name') ? (
                                  <button
                                    type="button"
                                    onClick={() => verifyField(draft.id, 'driver_name')}
                                    className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap"
                                  >
                                    Verify
                                  </button>
                                ) : (
                                  <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                    Verify
                                  </span>
                                )}
                              </div>
                              <input
                                type="text"
                                value={draft.data.driver_name}
                                onChange={(e) => handleFieldChange(draft.id, 'driver_name', e.target.value)}
                                ref={(el) => {
                                  fieldRefs.current[`${draft.id}:driver_name`] = el;
                                }}
                                className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                  isLowConfidence(draft, 'driver_name')
                                    ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                    : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                                } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'driver_name' ? 'ring-2 ring-primary-500/30' : ''}`}
                              />
                            </div>

                            {/* Driver Email */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  Driver Email
                                </label>
                                {isLowConfidence(draft, 'driver_email') ? (
                                  <button
                                    type="button"
                                    onClick={() => verifyField(draft.id, 'driver_email')}
                                    className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap"
                                  >
                                    Verify
                                  </button>
                                ) : (
                                  <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                    Verify
                                  </span>
                                )}
                              </div>
                              <input
                                type="email"
                                value={draft.data.driver_email || ''}
                                onChange={(e) => handleFieldChange(draft.id, 'driver_email', e.target.value)}
                                ref={(el) => {
                                  fieldRefs.current[`${draft.id}:driver_email`] = el;
                                }}
                                placeholder="Optional"
                                className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                  isLowConfidence(draft, 'driver_email')
                                    ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                    : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                                } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'driver_email' ? 'ring-2 ring-primary-500/30' : ''}`}
                              />
                            </div>

                            {/* Driver Phone */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  Driver Phone
                                </label>
                                {isLowConfidence(draft, 'driver_phone') ? (
                                  <button
                                    type="button"
                                    onClick={() => verifyField(draft.id, 'driver_phone')}
                                    className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap"
                                  >
                                    Verify
                                  </button>
                                ) : (
                                  <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                    Verify
                                  </span>
                                )}
                              </div>
                              <input
                                type="tel"
                                value={draft.data.driver_phone || ''}
                                onChange={(e) => handleFieldChange(draft.id, 'driver_phone', e.target.value)}
                                ref={(el) => {
                                  fieldRefs.current[`${draft.id}:driver_phone`] = el;
                                }}
                                placeholder="Optional"
                                className={`w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                  isLowConfidence(draft, 'driver_phone')
                                    ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                    : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                                } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'driver_phone' ? 'ring-2 ring-primary-500/30' : ''}`}
                              />
                            </div>

                            {/* Hire Reference */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  Hire Reference
                                </label>
                                <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                  Verify
                                </span>
                              </div>
                              <input
                                type="text"
                                value={draft.data.hire_ref || ''}
                                onChange={(e) => handleFieldChange(draft.id, 'hire_ref', e.target.value)}
                                placeholder="Optional"
                                className="w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-primary-100 focus:bg-white dark:focus:bg-slate-900 outline-none"
                              />
                            </div>

                            {/* Company Name */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  Company Name
                                </label>
                                <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                  Verify
                                </span>
                              </div>
                              <input
                                type="text"
                                value={draft.data.company_name || ''}
                                onChange={(e) => handleFieldChange(draft.id, 'company_name', e.target.value)}
                                placeholder="Optional"
                                className="w-full h-10 px-2 py-1.5 text-sm text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-primary-100 focus:bg-white dark:focus:bg-slate-900 outline-none"
                              />
                            </div>

                            {/* Start Date */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  Start Date *
                                </label>
                                {isLowConfidence(draft, 'start_date') ? (
                                  <button
                                    type="button"
                                    onClick={() => verifyField(draft.id, 'start_date')}
                                    className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap"
                                  >
                                    Verify
                                  </button>
                                ) : (
                                  <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                    Verify
                                  </span>
                                )}
                              </div>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={formatIsoToUkDate(String(draft.data.start_date || ''))}
                                  onChange={(e) =>
                                    handleFieldChange(draft.id, 'start_date', normalizeDateInput(e.target.value))
                                  }
                                  placeholder="DD/MM/YYYY"
                                  inputMode="numeric"
                                  ref={(el) => {
                                    fieldRefs.current[`${draft.id}:start_date`] = el;
                                  }}
                                  className={`w-full h-10 px-2 py-1.5 pr-9 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                    isInvalidDate(String(draft.data.start_date || ''))
                                      ? 'border-red-400 ring-red-100 dark:ring-red-900/30 bg-red-50/40 dark:bg-red-900/10'
                                      : isLowConfidence(draft, 'start_date')
                                        ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                        : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                                  } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'start_date' ? 'ring-2 ring-primary-500/30' : ''}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => setDatePicker({ draftId: draft.id, field: 'start_date' })}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
                                  title="Pick date"
                                >
                                  <Calendar size={14} />
                                </button>
                                {datePicker?.draftId === draft.id && datePicker.field === 'start_date' && (
                                  <div className="absolute right-0 mt-2 z-20 w-64 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-xs font-bold text-slate-700 dark:text-slate-200">
                                        Pick start date
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => setDatePicker(null)}
                                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400"
                                        title="Close"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                    <input
                                      type="date"
                                      value={isoToYyyyMmDd(String(draft.data.start_date || ''))}
                                      onChange={(e) => {
                                        const iso = yyyyMmDdToIso(e.target.value);
                                        handleFieldChange(draft.id, 'start_date', iso || '');
                                        setDatePicker(null);
                                      }}
                                      className="w-full h-10 px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                    />
                                    <div className="mt-2 flex items-center justify-between">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          handleFieldChange(draft.id, 'start_date', todayIsoUtc());
                                          setDatePicker(null);
                                        }}
                                        className="text-xs font-bold text-primary-700 dark:text-primary-300 hover:underline"
                                      >
                                        Today
                                      </button>
                                      <div className="text-[10px] text-slate-400">Saves as UK date</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* End Date */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1 h-4">
                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 truncate whitespace-nowrap">
                                  End Date
                                </label>
                                {isLowConfidence(draft, 'end_date') ? (
                                  <button
                                    type="button"
                                    onClick={() => verifyField(draft.id, 'end_date')}
                                    className="text-[10px] font-bold text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap"
                                  >
                                    Verify
                                  </button>
                                ) : (
                                  <span className="text-[10px] font-bold opacity-0 select-none whitespace-nowrap">
                                    Verify
                                  </span>
                                )}
                              </div>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={formatIsoToUkDate(String(draft.data.end_date || ''))}
                                  onChange={(e) =>
                                    handleFieldChange(draft.id, 'end_date', normalizeDateInput(e.target.value))
                                  }
                                  placeholder="DD/MM/YYYY"
                                  inputMode="numeric"
                                  ref={(el) => {
                                    fieldRefs.current[`${draft.id}:end_date`] = el;
                                  }}
                                  className={`w-full h-10 px-2 py-1.5 pr-9 text-sm text-slate-900 dark:text-white border rounded bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:bg-white dark:focus:bg-slate-900 outline-none ${
                                    isInvalidDate(String(draft.data.end_date || ''))
                                      ? 'border-red-400 ring-red-100 dark:ring-red-900/30 bg-red-50/40 dark:bg-red-900/10'
                                      : isLowConfidence(draft, 'end_date')
                                        ? 'border-amber-400 ring-amber-100 dark:ring-amber-900/30 bg-amber-50 dark:bg-amber-900/20'
                                        : 'border-slate-200 dark:border-slate-700 focus:ring-primary-100'
                                  } ${verifyTarget?.draftId === draft.id && verifyTarget.field === 'end_date' ? 'ring-2 ring-primary-500/30' : ''}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => setDatePicker({ draftId: draft.id, field: 'end_date' })}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
                                  title="Pick date"
                                >
                                  <Calendar size={14} />
                                </button>
                                {datePicker?.draftId === draft.id && datePicker.field === 'end_date' && (
                                  <div className="absolute right-0 mt-2 z-20 w-64 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-xs font-bold text-slate-700 dark:text-slate-200">
                                        Pick end date
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => setDatePicker(null)}
                                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400"
                                        title="Close"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                    <input
                                      type="date"
                                      value={isoToYyyyMmDd(String(draft.data.end_date || ''))}
                                      onChange={(e) => {
                                        const iso = yyyyMmDdToIso(e.target.value);
                                        handleFieldChange(draft.id, 'end_date', iso || '');
                                        setDatePicker(null);
                                      }}
                                      className="w-full h-10 px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                    />
                                    <div className="mt-2 flex items-center justify-between">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          handleFieldChange(draft.id, 'end_date', todayIsoUtc());
                                          setDatePicker(null);
                                        }}
                                        className="text-xs font-bold text-primary-700 dark:text-primary-300 hover:underline"
                                      >
                                        Today
                                      </button>
                                      <div className="text-[10px] text-slate-400">Saves as UK date</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Additional Info */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-200 dark:border-slate-800">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`liability-${draft.id}`}
                                checked={draft.data.has_liability_clause}
                                onChange={(e) => handleFieldChange(draft.id, 'has_liability_clause', e.target.checked)}
                                className="w-4 h-4 rounded border-slate-300 dark:border-slate-700 text-primary-600 focus:ring-2 focus:ring-primary-100"
                              />
                              <label
                                htmlFor={`liability-${draft.id}`}
                                className="text-sm text-slate-700 dark:text-slate-300"
                              >
                                Liability clause present (TMA 2004 / PoFA 2012)
                              </label>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`signature-${draft.id}`}
                                checked={draft.data.has_signature}
                                onChange={(e) => handleFieldChange(draft.id, 'has_signature', e.target.checked)}
                                className="w-4 h-4 rounded border-slate-300 dark:border-slate-700 text-primary-600 focus:ring-2 focus:ring-primary-100"
                              />
                              <label
                                htmlFor={`signature-${draft.id}`}
                                className="text-sm text-slate-700 dark:text-slate-300"
                              >
                                Driver signature visible
                              </label>
                            </div>
                          </div>

                          {/* Action Button */}
                          <div className="flex justify-end pt-4">
                            <button
                              onClick={() => handleSaveDraft(draft.id)}
                              className="px-6 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
                            >
                              <Save size={16} />
                              Save to New Hire
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Skeleton for processing */}
                      {(draft.status === 'processing' || draft.status === 'pending') && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
                          <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
                          <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
                          <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop docked preview (keeps fields editable) */}
              <div className="hidden lg:flex w-[420px] xl:w-[520px] border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <div className="w-full flex flex-col">
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40">
                    <div className="text-xs text-slate-500 dark:text-slate-400">Agreement viewer</div>
                    <div className="text-sm font-bold text-slate-900 dark:text-white truncate">
                      {previewDraft ? previewDraft.fileName : 'Select a file to preview'}
                    </div>
                  </div>

                  <div className="p-4 flex-1 overflow-hidden">
                    {!previewDraft ? (
                      <div className="h-full flex items-center justify-center text-center text-slate-500 dark:text-slate-400 px-6">
                        Click "Preview" on a file to view it while editing fields.
                      </div>
                    ) : (
                      <div className="flex flex-col h-full">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={zoomOut}
                              className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                            >
                              <ZoomOut size={14} /> Zoom
                            </button>
                            <button
                              type="button"
                              onClick={zoomIn}
                              className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                            >
                              <ZoomIn size={14} /> Zoom
                            </button>
                            <button
                              type="button"
                              onClick={rotate}
                              className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                            >
                              <RotateCw size={14} /> Rotate
                            </button>
                            <button
                              type="button"
                              onClick={resetView}
                              className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            >
                              Reset
                            </button>
                            <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                              {Math.round(previewZoom * 100)}%
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {previewUrl && (
                              <>
                                <a
                                  href={previewUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-2"
                                >
                                  <ExternalLink size={14} />
                                  Open
                                </a>
                                <button
                                  type="button"
                                  onClick={() => setPreviewDraftId(null)}
                                  className="px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                                >
                                  Hide
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex-1 bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                          {previewUrl && (
                            <>
                              {previewIsPdf ? (
                                <iframe title="PDF preview" src={previewUrl} className="w-full h-full" />
                              ) : previewCanRenderImage ? (
                                <div
                                  className="w-full h-full relative overflow-hidden cursor-grab active:cursor-grabbing"
                                  onMouseDown={(e) => {
                                    panStartRef.current = {
                                      startX: e.clientX,
                                      startY: e.clientY,
                                      originX: previewPan.x,
                                      originY: previewPan.y
                                    };
                                  }}
                                  onMouseMove={(e) => {
                                    if (!panStartRef.current) return;
                                    const dx = e.clientX - panStartRef.current.startX;
                                    const dy = e.clientY - panStartRef.current.startY;
                                    setPreviewPan({
                                      x: panStartRef.current.originX + dx,
                                      y: panStartRef.current.originY + dy
                                    });
                                  }}
                                  onMouseUp={() => {
                                    panStartRef.current = null;
                                  }}
                                  onMouseLeave={() => {
                                    panStartRef.current = null;
                                  }}
                                >
                                  <img
                                    src={previewUrl}
                                    alt="Preview"
                                    className="absolute left-1/2 top-1/2 max-w-none select-none"
                                    draggable={false}
                                    style={{
                                      transform: `translate(calc(-50% + ${previewPan.x}px), calc(-50% + ${previewPan.y}px)) scale(${previewZoom}) rotate(${previewRotation}deg)`,
                                      transformOrigin: 'center'
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-500 dark:text-slate-400">
                                  Preview not available for this file type.
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {!previewIsPdf && (
                          <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                            Tip: drag to pan • use zoom/rotate to verify flagged fields
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0 flex items-center justify-between">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {drafts.filter(d => d.status === 'success').length} processed,{' '}
                {drafts.filter(d => d.data?.low_confidence_fields?.length).length} flagged for review
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default HireAgreementExtractModal;
